import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import escochexLogo from '../../../assets/escochex-logo.webp';
import { EmployeeSignOut } from './EmployeeSignOut';
import { QuestionsProvider } from '../questions/QuestionsProvider';
import { ONBOARDING_SECTIONS, sectionPath, type OnboardingSectionSlug } from '../types';
import '../employee-onboarding.css';

const STEPS = ['Employee Setup', 'Taxes & Exemptions', 'Upload Documents'];

export function ProgressSteps({ activeStep }: { activeStep: 1 | 2 | 3 }) {
  return (
    <ol className="eo-steps" aria-label="Onboarding progress">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const state = n === activeStep ? 'active' : n < activeStep ? 'done' : 'todo';
        return (
          <li key={label} className={`eo-steps__item eo-steps__item--${state}`} aria-current={n === activeStep ? 'step' : undefined}>
            <span className="eo-steps__circle" aria-hidden="true">
              {n}
            </span>
            <span className="eo-steps__label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export interface LayoutNavItem {
  key: string;
  label: string;
  to: string;
}

/** A left navigation other than the Step 1 one (Step 2 passes its four tax sections here). */
export interface LayoutNavigation {
  ariaLabel: string;
  items: readonly LayoutNavItem[];
  activeKey: string;
  /** Index of the last item the employee may open (items after it are not links yet). */
  reachableIndex: number;
}

interface OnboardingLayoutProps {
  activeStep: 1 | 2 | 3;
  /** Overrides the Step 1 navigation (used by Step 2). */
  navigation?: LayoutNavigation | undefined;
  /** The section highlighted in the left navigation; omit to render no navigation (Step 2 placeholder). */
  activeSection?: OnboardingSectionSlug | undefined;
  /** Index of the last section the employee may open (sections after it are not links yet). */
  reachableIndex?: number;
  children: ReactNode;
}

/** The gray page + white rounded card shared by every Step 1 screen: title, 3-step progress, logo, left section nav, content. */
export function OnboardingLayout({ activeStep, navigation, activeSection, reachableIndex = ONBOARDING_SECTIONS.length - 1, children }: OnboardingLayoutProps) {
  const nav: LayoutNavigation | null = navigation
    ?? (activeSection
      ? {
          ariaLabel: 'Employee Setup sections',
          items: ONBOARDING_SECTIONS.map((s) => ({ key: s.slug, label: s.label, to: sectionPath(s.slug) })),
          activeKey: activeSection,
          reachableIndex,
        }
      : null);
  return (
    <div className="eo-page">
      <div className="eo-card">
        <header className="eo-card__header">
          <h1 className="eo-card__title">Employee Profile Setup</h1>
          <ProgressSteps activeStep={activeStep} />
          <div className="eo-card__brand">
            <img className="eo-card__logo" src={escochexLogo} alt="Escochex — A Payroll Software Company" />
            <EmployeeSignOut />
          </div>
        </header>
        <div className={`eo-card__body${nav ? '' : ' eo-card__body--single'}`}>
          {nav && (
            <nav className={`eo-nav${navigation ? ' eo-nav--ruled' : ''}`} aria-label={nav.ariaLabel}>
              <ul>
                {nav.items.map((item, i) => {
                  const current = item.key === nav.activeKey;
                  if (i > nav.reachableIndex) {
                    return (
                      <li key={item.key}>
                        <span className="eo-nav__link eo-nav__link--disabled" aria-disabled="true">
                          {item.label}
                        </span>
                      </li>
                    );
                  }
                  return (
                    <li key={item.key}>
                      <Link to={item.to} className={`eo-nav__link${current ? ' eo-nav__link--active' : ''}`} aria-current={current ? 'page' : undefined}>
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          )}
          <div className="eo-panel-wrap"><QuestionsProvider>{children}</QuestionsProvider></div>
        </div>
      </div>
    </div>
  );
}
